import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    makeStyles,
    MenuItem,
    TextField,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { addReport, ReportModel, updateReport } from '../../redux/actions/dashboard'
import { Tenant } from '../../shared/types/tenant'

const isValidUrl = (url: string) => /^https?:\/\/\S+$/i.test(url.trim())

const NO_TENANT = '__none__'

const useStyles = makeStyles((theme) => ({
    field: {
        marginTop: theme.spacing(1),
        width: 480,
        display: 'block',
    },
}))

interface Props {
    open: boolean
    // undefined = novy report
    report?: ReportModel
    // predvyplneny tenant pri novom reporte
    defaultTenantId?: string | null
    tenants: Tenant[]
    onClose: () => void
    onSaved: () => void
    onError: (msg: string) => void
}

export default function ReportDialog({ open, report, defaultTenantId, tenants, onClose, onSaved, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [url, setUrl] = useState('')
    const [description, setDescription] = useState('')
    const [tenantId, setTenantId] = useState<string>(NO_TENANT)
    const [saving, setSaving] = useState(false)
    const [touched, setTouched] = useState(false)

    useEffect(() => {
        if (!open) return
        setName(report?.name ?? '')
        setUrl(report?.url ?? '')
        setDescription(report?.description ?? '')
        setTenantId((report ? report.tenantId : defaultTenantId) || NO_TENANT)
        setTouched(false)
    }, [open, report, defaultTenantId])

    const nameError = touched && !name.trim()
    const urlError = touched && !isValidUrl(url)
    const valid = Boolean(name.trim()) && isValidUrl(url)

    const close = () => {
        if (!saving) onClose()
    }

    const handleSave = async () => {
        setTouched(true)
        if (!valid) return
        const body: ReportModel = {
            name: name.trim(),
            url: url.trim(),
            description: description.trim() || null,
            tenantId: tenantId === NO_TENANT ? null : tenantId,
        }
        setSaving(true)
        try {
            if (report?.id) {
                await updateReport(report.id, body)
            } else {
                await addReport(body)
            }
            onSaved()
        } catch {
            onError(t('dashboard.errorSavingReport'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle>{report ? t('dashboard.editReport') : t('dashboard.addReport')}</DialogTitle>
            <DialogContent>
                <TextField
                    select
                    fullWidth
                    className={classes.field}
                    variant="filled"
                    label={t('common.tenant')}
                    value={tenantId}
                    onChange={(e) => setTenantId(e.target.value as string)}
                >
                    <MenuItem value={NO_TENANT}>
                        <em>{t('dashboard.withoutTenant')}</em>
                    </MenuItem>
                    {tenants.map((tenant) => (
                        <MenuItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    autoFocus
                    fullWidth
                    required
                    className={classes.field}
                    variant="filled"
                    label={t('common.name')}
                    value={name}
                    inputProps={{ maxLength: 64 }}
                    error={nameError}
                    helperText={nameError ? t('common.required') : ' '}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    fullWidth
                    required
                    className={classes.field}
                    variant="filled"
                    label={t('reports.url')}
                    placeholder="https://"
                    value={url}
                    inputProps={{ maxLength: 256 }}
                    error={urlError}
                    helperText={urlError ? t('dashboard.invalidUrl') : ' '}
                    onChange={(e) => setUrl(e.target.value)}
                />
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    className={classes.field}
                    variant="filled"
                    label={t('common.description')}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={20} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
